import type { ShippingOptionContentVariantKeys } from '@/components/order/shippingOptions/ShippingOptionCard/types';
import { getContent } from '@/components/order/shippingOptions/ShippingOptionCard/utils';
import { Logger } from '@/utils/logger';
import { DELIVERY_OPTION, DELIVERY_OPTIONS, getAdditionalTemporalInfo } from '@montugroup/circuit-shared';
import type { ArrayElement } from '@montugroup/circuit-shared/utils/typscript-helpers';
import { Typography } from '@montugroup/design-system';
import StoreIcon from '@mui/icons-material/Store';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import { useFeatureFlag } from '../useFeatureFlag';
import type { DeliveryOptions } from './types';

const logger = new Logger('useClickAndCollectDeliveryOption');

// TODO: CONTENT_VARIANTS to be sourced from LaunchDarkly
const CONTENT_VARIANTS: ShippingOptionContentVariantKeys = {
  title: 'A',
  description: 'A',
  icon: 'A',
};

const CONTENT = {
  icons: { A: <StoreIcon /> },
  titles: { A: DELIVERY_OPTIONS[DELIVERY_OPTION.ClickAndCollect].displayName },
  descriptions: {
    A: () => {
      return <Typography>Select a location</Typography>;
    },
  },
} as const;

const getSettings = (option?: ArrayElement<DeliveryOptions['ClickAndCollect']>) => {
  if (!option) {
    return null;
  }

  const content = getContent<typeof CONTENT>({
    content: CONTENT,
    variants: CONTENT_VARIANTS,
    // TODO: Figure out how to not need to pass anything for descriptionArgs if the description function doesn't have any required args
    descriptionArgs: undefined,
  });

  return {
    ...option,
    ...content,
    // NOTE: It's UNKNOWN because this hook is used on the shipping options page not the C&C map page, so the user can't see the ETA at this point (it is also related to 3 C&C options and the ETA could be different for some options)
    estimatedArrivalTime: 'UNKNOWN',
  };
};

export default function useClickAndCollectDeliveryOption(props: DeliveryOptions['ClickAndCollect']) {
  const isFlagEnabled = Boolean(useFeatureFlag('enable_click_collect_shipping_option'));
  const settings = useMemo(() => getSettings(props[0]), [props]);
  const shouldShow = !!settings && isFlagEnabled;

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { icon, description, ...loggableSettings } = settings ?? {};

    const additionalTemporalLogs = {
      browserTime: {
        ...getAdditionalTemporalInfo(DateTime.now().toISO()),
      },
      validBefore: {
        ...getAdditionalTemporalInfo(settings?.validBefore),
      },
    };

    logger.info('settings', {
      ...loggableSettings,
      shouldShow,
      additionalTemporalLogs,
      props,
    });
  }, [settings, props, shouldShow]);

  return shouldShow ? settings : null;
}
